// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Clock, Users, Info } from 'lucide-react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './routes/AppRoutes';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './components/common/LoginPage';
import GlobalNavigation from './components/common/GlobalNavigation';
// MVP Configuration - RBAC simplification (PE-737)
import { getMVPDefaultRole, shouldBypassRoleChecks } from './config/mvpConfig';
// Import storage backup utilities (adds to window for console access)
import './utils/storageBackup';
// Import field cache utilities (adds clearFieldCache to window for console access)
import './utils/clearFieldCache';
// Import list view debug utilities (adds debugListViews, resetListViews to window)
import './utils/debugListViews';
// Import system validation utilities (adds validateSystem to window)
import './utils/systemValidation';
// Import system integration test (adds testSystem to window)
import './utils/systemIntegrationTest';
// Import object cache clearing utility (adds clearObjectCache to window)
import './utils/clearObjectCache';
// Import field migration utilities
import { addCreditScoreFieldsToClient } from './utils/addCreditScoreFields';
import { seedCreditBlockWorkflows } from './data/creditBlockWorkflowRules';
import ClientDetail from './components/common/ClientDetail';
import CreditManagementApp from './pages/credit-management/CreditManagementApp';
import CyberSweepApp from './pages/cybersweep/CyberSweepApp';
import CreditBlockApp from './pages/credit-block/CreditBlockApp';
import UserManagement from './pages/admin/UserManagement';
import UIConfigurationManager from './pages/admin/UIConfigurationManager';
import UserProfile from './components/common/UserProfile';
import Settings from './components/common/Settings';
import SystemAdmin from './components/common/SystemAdmin';
import SupportTickets from './components/common/SupportTickets';
import DynamicFormSectionTest from './pages/test/DynamicFormSectionTest';
// ComponentLibraryDemo moved to UIConfigurationManager as a tab
import { ToastProvider as StandardToastProvider } from './components/standard';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ApplicationProvider } from './contexts/ApplicationContext';
// mockData import removed - using persistedTaskRecords and persistedClientRecords for dynamic data
import { persistedTaskRecords } from './services/mockData/tasks';
import { persistedClientRecords } from './services/mockData/clients';
import CustomerServiceChatbot from './components/common/CustomerServiceChatbot';
// PE-762: Switch User Context and Banner
import { SwitchUserProvider, useSwitchUser } from './contexts/SwitchUserContext';
import { AbilityProvider } from './contexts/AbilityContext';
import { SwitchUserBanner } from './components/common/SwitchUserBanner';
import { KeyboardShortcutsModal } from './components/common/KeyboardShortcutsModal';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { ClientListView } from './components/lists/ClientListView';
import { LeadListView } from './components/lists/LeadListView';
import { ContactListView } from './components/lists/ContactListView';
import { OpportunityListView } from './components/lists/OpportunityListView';
import { CaseListView } from './components/lists/CaseListView';
import { EventListView } from './components/lists/EventListView';
import { ReportListView } from './components/lists/ReportListView';
import { TaskListView } from './components/lists/TaskListView';
import { LeadDetailPage } from './pages/detail/LeadDetailPage';
import { ClientDetailPage } from './pages/detail/ClientDetailPage';
import { OpportunityDetailPage } from './pages/detail/OpportunityDetailPage';
import { ContactDetailPage } from './pages/detail/ContactDetailPage';
import { CaseDetailPage } from './pages/detail/CaseDetailPage';
import { EventDetailPage } from './pages/detail/EventDetailPage';
import { TaskDetailPage } from './pages/detail/TaskDetailPage';
import { ReportDetailPage } from './pages/detail/ReportDetailPage';
import { queryCachePersister, queryClient } from './queryClient';
import { CREDIT_BLOCK_CASE_ROW_CLIENT_QUERY_KEY } from './services/queries/creditBlockCaseQueries';

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user, hasRole } = useAuth();
  // PE-835: Get switch user state for banner offset
  const { isSwitched } = useSwitchUser();
  // PE-835: Conditional padding - extra space when View As User banner is active
  // Banner height is 44px (top-11), so add 44px to each padding variant
  const contentPadding = isSwitched ? 'pt-[108px]' : 'pt-16';      // 64px / 108px
  const contentPadding20 = isSwitched ? 'pt-[124px]' : 'pt-20';    // 80px / 124px
  const contentPadding24 = isSwitched ? 'pt-[140px]' : 'pt-24';    // 96px / 140px
  
  // Get user role from Keycloak, fallback to MVP default
  const getUserRole = (): string => {
    if (user) {
      // Map Keycloak roles to app roles
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };

  // All hooks must be called before any early returns
  const [currentView, setCurrentView] = useState('component-library-demo');
  // Use Keycloak role if available, otherwise fallback to MVP default
  const [userRole, setUserRole] = useState(getUserRole());
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const { isShortcutsModalOpen, setIsShortcutsModalOpen } = useKeyboardShortcuts();

  // Update userRole when user changes
  useEffect(() => {
    if (user) {
      const newRole = getUserRole();
      setUserRole(newRole);
    }
  }, [user, hasRole]);

  // Run field migrations and seed data on app load
  useEffect(() => {
    try {
      addCreditScoreFieldsToClient();
      seedCreditBlockWorkflows();
    } catch (error) {
      console.error('Initialization error:', error);
    }
  }, []);

  // Show login page if not authenticated (after all hooks)
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const handleAppSelect = (app: any) => {
    if (app.name === 'home') {
      setCurrentView('home');
    } else {
      setCurrentView('app-' + app.name.toLowerCase().replace(' ', '-'));
    }
  };

  // Navigation handlers for notifications
  const handleNavigateToTask = (taskId: string) => {
    setSelectedTaskId(taskId);
    setCurrentView('task-detail');
  };

  const handleNavigateToCase = (caseId: string) => {
    setSelectedCaseId(caseId);
    setCurrentView('case-detail');
  };

  // Render different views based on currentView
  if (currentView === 'app-credit-management') {
    return (
      <div>
        <GlobalNavigation
          currentView="credit-management"
          onNavigate={(view) => {
            if (view === 'home') {
              setCurrentView('home');
            } else {
              setCurrentView(view);
            }
          }}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={false}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <CreditManagementApp
            onBackToHome={() => setCurrentView('home')}
            userRole={userRole}
          />
        </div>
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  if (currentView === 'app-cybersweep') {
    return (
      <div>
        <GlobalNavigation
          currentView="app-cybersweep"
          onNavigate={(view) => {
            if (view === 'home') {
              setCurrentView('home');
            } else {
              setCurrentView(view);
            }
          }}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={false}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <CyberSweepApp
            onBackToHome={() => setCurrentView('home')}
            userRole={userRole}
          />
        </div>
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  if (currentView === 'app-credit-block') {
    return (
      <div>
        <GlobalNavigation
          currentView="app-credit-block"
          onNavigate={(view) => {
            if (view === 'home') {
              setCurrentView('home');
            } else {
              setCurrentView(view);
            }
          }}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={false}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <CreditBlockApp
            onBackToHome={() => setCurrentView('home')}
            userRole={userRole}
          />
        </div>
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // User Management view
  if (currentView === 'user-management') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <UserManagement />
        </div>
      </div>
    );
  }

  // UI Configuration Manager view
  if (currentView === 'ui-configuration') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <UIConfigurationManager userRole={userRole} />
        </div>
      </div>
    );
  }

  // Dynamic Form Section Test view
  if (currentView === 'dynamic-form-test') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <DynamicFormSectionTest />
        </div>
      </div>
    );
  }

  // User Profile view
  if (currentView === 'profile') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <UserProfile userRole={userRole} />
        </div>
      </div>
    );
  }

  // Settings view
  if (currentView === 'settings') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <Settings userRole={userRole} />
        </div>
      </div>
    );
  }

  // System Admin view
  if (currentView === 'system-admin') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <SystemAdmin userRole={userRole} onNavigate={setCurrentView} onRoleChange={setUserRole} />
        </div>

        {/* Customer Service Chatbot - Available in System Admin */}
        {(userRole === 'customer_service' || userRole === 'supervisor' || userRole === 'manager' || userRole === 'cb_supervisor' || userRole === 'cm_supervisor') && (
          <CustomerServiceChatbot
            userRole={userRole}
            userName="CS Agent"
            userDepartment="Customer Service"
            onClientLookup={(clientId) => {
              // Use persisted client data (dynamic)
              const allClients = persistedClientRecords.get();
              const client = allClients.find(c => c.id === clientId);
              if (client) {
                setSelectedClient(client);
                setCurrentView('client-detail');
              }
            }}
            onEscalation={(issue) => {
              console.log('Escalation requested:', issue);
            }}
            onTransfer={(department) => {
              console.log('Transfer to department:', department);
            }}
            position="bottom-right"
          />
        )}

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Support Tickets view
  if (currentView === 'support-tickets') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <SupportTickets userRole={userRole} currentUser="Current User" />
        </div>
      </div>
    );
  }

  // Helper functions to get filtered data - using real persisted data
  const getCurrentUserTasks = () => {
    // Get tasks from persisted storage (dynamic, survives refresh)
    const allTasks = persistedTaskRecords.get();
    // For now, simulate current user as "John Smith"
    // Filter to current user AND validate task has required fields
    return allTasks.filter(task => {
      // Validate task has required fields (filter out phantom/invalid tasks)
      if (!task || !task.id || !task.title || !task.status) {
        return false;
      }
      return task.assignee === 'John Smith';
    });
  };

  const getOpenTasks = () => getCurrentUserTasks().filter(task => task.status !== 'Completed');
  const getDueSoonTasks = () => {
    const today = new Date();
    const threeDaysFromNow = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);
    return getCurrentUserTasks().filter(task => {
      const dueDate = new Date(task.dueDate);
      return dueDate <= threeDaysFromNow && task.status !== 'Completed';
    });
  };
  const getCompletedTasks = () => getCurrentUserTasks().filter(task => task.status === 'Completed');

  // Manager Dashboard data - using real data from localStorage
  const getTeamStats = () => {
    const allTasks = persistedTaskRecords.get();
    const allClients = persistedClientRecords.get();

    const totalTasks = allTasks.length;
    const completedTasks = allTasks.filter(t => t.status === 'Completed').length;
    const highPriorityTasks = allTasks.filter(t => t.priority === 'High' && t.status !== 'Completed').length;
    const overdueTasks = allTasks.filter(t => {
      const dueDate = new Date(t.dueDate || '');
      const today = new Date();
      return dueDate < today && t.status !== 'Completed';
    }).length;

    return {
      totalTasks,
      completedTasks,
      completionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
      highPriorityTasks,
      overdueTasks,
      activeClients: allClients.filter(c => c.status === 'Active').length,
      totalClients: allClients.length
    };
  };

  const getTeamPerformance = () => {
    const allTasks = persistedTaskRecords.get();
    const uniqueAssignees = new Set(allTasks.map(t => t.assignee).filter(Boolean));
    const assignees = Array.from(uniqueAssignees);
    return assignees.map(assignee => {
      const tasks = allTasks.filter(t => t.assignee === assignee);
      const completed = tasks.filter(t => t.status === 'Completed').length;
      const total = tasks.length;
      return {
        name: assignee,
        completed,
        total,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
        highPriority: tasks.filter(t => t.priority === 'High' && t.status !== 'Completed').length
      };
    });
  };

  // Render Client Detail view
  if (currentView === 'client-detail' && selectedClient) {
    return (
      <div>
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <ClientDetail
            client={selectedClient}
            onBack={() => {
              setCurrentView('clients');
              setSelectedClient(null);
            }}
          />
        </div>
      </div>
    );
  }

  // Render Client Detail view
  if (currentView === 'client-detail' && selectedClientId) {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView="clients"
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <ClientDetailPage
            id={selectedClientId}
            onBack={() => {
              setCurrentView('clients');
              setSelectedClientId(null);
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Clients view
  if (currentView === 'clients') {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <ClientListView
            onNavigateToClient={(clientId) => {
              setSelectedClientId(clientId);
              setCurrentView('client-detail');
            }}
            onCreateClient={() => {
              console.log('Create new client - to be implemented');
              // TODO: Implement client creation modal
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Lead Detail view
  if (currentView === 'lead-detail' && selectedLeadId) {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView="leads"
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <LeadDetailPage
            id={selectedLeadId}
            onBack={() => {
              setCurrentView('leads');
              setSelectedLeadId(null);
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Leads view
  if (currentView === 'leads') {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <LeadListView
            onNavigateToLead={(leadId) => {
              setSelectedLeadId(leadId);
              setCurrentView('lead-detail');
            }}
            onCreateLead={() => {
              console.log('Create new lead - to be implemented');
              // TODO: Implement lead creation modal
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Opportunity Detail view
  if (currentView === 'opportunity-detail' && selectedOpportunityId) {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView="opportunities"
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <OpportunityDetailPage
            id={selectedOpportunityId}
            onBack={() => {
              setCurrentView('opportunities');
              setSelectedOpportunityId(null);
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Opportunities view
  if (currentView === 'opportunities') {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <OpportunityListView
            onNavigateToOpportunity={(opportunityId) => {
              setSelectedOpportunityId(opportunityId);
              setCurrentView('opportunity-detail');
            }}
            onCreateOpportunity={() => {
              console.log('Create new opportunity - to be implemented');
              // TODO: Implement opportunity creation modal
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Contact Detail view
  if (currentView === 'contact-detail' && selectedContactId) {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView="contacts"
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <ContactDetailPage
            id={selectedContactId}
            onBack={() => {
              setCurrentView('contacts');
              setSelectedContactId(null);
            }}
            onNavigateToClient={(clientId) => {
              setSelectedClientId(clientId);
              setCurrentView('client-detail');
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Contacts view
  if (currentView === 'contacts') {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <ContactListView
            onNavigateToContact={(contactId) => {
              setSelectedContactId(contactId);
              setCurrentView('contact-detail');
            }}
            onCreateContact={() => {
              console.log('Create new contact - to be implemented');
              // TODO: Implement contact creation modal
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Case Detail view
  if (currentView === 'case-detail' && selectedCaseId) {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView="cases"
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <CaseDetailPage
            id={selectedCaseId}
            onBack={() => {
              setCurrentView('cases');
              setSelectedCaseId(null);
            }}
            onNavigateToClient={(clientId) => {
              setSelectedClientId(clientId);
              setCurrentView('client-detail');
            }}
            onNavigateToContact={(contactId) => {
              setSelectedContactId(contactId);
              setCurrentView('contact-detail');
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Cases view
  if (currentView === 'cases') {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <CaseListView
            onNavigateToCase={(caseId) => {
              setSelectedCaseId(caseId);
              setCurrentView('case-detail');
            }}
            onCreateCase={() => {
              console.log('Create new case - to be implemented');
              // TODO: Implement case creation modal
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Event Detail view
  if (currentView === 'event-detail' && selectedEventId) {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView="events"
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <EventDetailPage
            id={selectedEventId}
            onBack={() => {
              setCurrentView('events');
              setSelectedEventId(null);
            }}
          />
        </div>
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Events view
  if (currentView === 'events') {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <EventListView
            onNavigateToEvent={(eventId) => {
              setSelectedEventId(eventId);
              setCurrentView('event-detail');
            }}
            onCreateEvent={() => {
              console.log('Create new event - to be implemented');
              // TODO: Implement event creation modal
            }}
          />
        </div>

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Report Detail view
  if (currentView === 'report-detail' && selectedReportId) {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView="reports"
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <ReportDetailPage
            id={selectedReportId}
            onBack={() => {
              setCurrentView('reports');
              setSelectedReportId(null);
            }}
          />
        </div>
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Reports view
  if (currentView === 'reports') {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <div className={contentPadding}>
          <ReportListView
            onNavigateToReport={(reportId) => {
              setSelectedReportId(reportId);
              setCurrentView('report-detail');
            }}
            onCreateReport={() => {
              console.log('Create new report - to be implemented');
              // TODO: Implement report creation modal
            }}
          />
        </div>

        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Task Detail view
  if (currentView === 'task-detail' && selectedTaskId) {
    return (
      <div className="min-h-screen bg-[#0f172a]">
        <GlobalNavigation
          currentView="tasks"
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
        <div className={contentPadding}>
          <TaskDetailPage
            id={selectedTaskId}
            onBack={() => {
              setCurrentView('tasks');
              setSelectedTaskId(null);
            }}
          />
        </div>
        <KeyboardShortcutsModal
          isOpen={isShortcutsModalOpen}
          onClose={() => setIsShortcutsModalOpen(false)}
        />
      </div>
    );
  }

  // Render Tasks view
  if (currentView === 'tasks') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <main className={contentPadding20}>
          <TaskListView
            onNavigateToTask={(taskId) => {
              setSelectedTaskId(taskId);
              setCurrentView('task-detail');
            }}
            onCreateTask={() => {
              console.log('Create new task');
              // TODO: Implement task creation
            }}
          />
        </main>
      </div>
    );
  }

  // Manager Dashboard view
  if (currentView === 'dashboard') {
    const teamStats = getTeamStats();
    const teamPerformance = getTeamPerformance();

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <GlobalNavigation
          currentView={currentView}
          onNavigate={setCurrentView}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={true}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />

        <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${contentPadding24}`}>
          <div className="mb-8 flex items-center gap-4">
            <img 
              src="/regal-logo.svg" 
              alt="Regal Product Hub" 
              className="w-12 h-12"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Team Dashboard</h1>
              <p className="text-gray-600 dark:text-gray-400">Overview of team performance and metrics</p>
            </div>
          </div>

          {/* Team Performance Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completion Rate</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{teamStats.completionRate}%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{teamStats.completedTasks} of {teamStats.totalTasks} tasks</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Clients</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{teamStats.activeClients}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">of {teamStats.totalClients} total</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Priority</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{teamStats.highPriorityTasks}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">urgent items</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Overdue</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{teamStats.overdueTasks}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">past due</p>
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Team Performance */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Team Performance</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {teamPerformance.map((member) => (
                    <div key={member.name} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.name}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{member.completionRate}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${member.completionRate}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>{member.completed}/{member.total} completed</span>
                          {member.highPriority > 0 && (
                            <span className="text-red-600 dark:text-red-400">{member.highPriority} high priority</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Priority Distribution */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Priority Distribution</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {['High', 'Medium', 'Low'].map((priority) => {
                    const allTasks = persistedTaskRecords.get();
                    const count = allTasks.filter(t => t.priority === priority && t.status !== 'Completed').length;
                    const total = allTasks.filter(t => t.status !== 'Completed').length;
                    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

                    return (
                      <div key={priority} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{priority} Priority</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{count} tasks</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                priority === 'High' ? 'bg-red-500' :
                                priority === 'Medium' ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Team Activity */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Team Activity</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Task</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assignee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Due Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {(() => {
                    const tasks = persistedTaskRecords.get();
                    const sortedTasks = [...tasks].sort((a, b) => {
                      const dateA = new Date(a.lastModifiedDate || a.createdDate || '');
                      const dateB = new Date(b.lastModifiedDate || b.createdDate || '');
                      return dateB.getTime() - dateA.getTime();
                    });
                    return sortedTasks.slice(0, 10);
                  })().map((task) => (
                    <tr key={task.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{task.title}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{task.assignee}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          task.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          task.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {task.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          task.priority === 'High' ? 'bg-red-100 text-red-800' :
                          task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{task.dueDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Home view (Employee view)
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <GlobalNavigation
        currentView={currentView}
        onNavigate={setCurrentView}
        userRole={userRole}
        onRoleChange={setUserRole}
        showMainNavigation={true}
        onAppSelect={handleAppSelect}
      />

      {/* Main Content */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${contentPadding24}`}>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Work</h1>
          <p className="text-gray-600 dark:text-gray-400">Your personal task overview and assignments</p>
        </div>

        {/* My Work Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open Tasks</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getOpenTasks().length}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Due Soon</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getDueSoonTasks().length}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getCompletedTasks().length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Priority</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getCurrentUserTasks().filter(t => t.priority === 'High' && t.status !== 'Completed').length}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Open Tasks */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Open Tasks</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {getOpenTasks().slice(0, 5).map((task) => (
                  <button
                    key={task.id}
                    onClick={() => handleNavigateToTask(task.id)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer text-left"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{task.type} • Due: {task.dueDate}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      task.priority === 'High' ? 'bg-red-100 text-red-800' :
                      task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority}
                    </span>
                  </button>
                ))}
                {getOpenTasks().length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No open tasks</p>
                )}
              </div>
            </div>
          </div>

          {/* Due Soon */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Due Soon</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {getDueSoonTasks().slice(0, 5).map((task) => (
                  <button
                    key={task.id}
                    onClick={() => handleNavigateToTask(task.id)}
                    className="w-full flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors cursor-pointer text-left"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{task.type} • Due: {task.dueDate}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      task.priority === 'High' ? 'bg-red-100 text-red-800' :
                      task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority}
                    </span>
                  </button>
                ))}
                {getDueSoonTasks().length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No tasks due soon</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Activity</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {getCurrentUserTasks().slice(0, 8).map((task) => (
                <button
                  key={task.id}
                  onClick={() => handleNavigateToTask(task.id)}
                  className="w-full flex items-start space-x-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer text-left"
                >
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    task.status === 'Completed' ? 'bg-green-500' :
                    task.status === 'In Progress' ? 'bg-blue-500' :
                    'bg-yellow-500'
                  }`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {task.type} •
                      <span className={`${
                        task.status === 'Completed' ? 'text-green-600 dark:text-green-400' :
                        task.status === 'In Progress' ? 'text-blue-600 dark:text-blue-400' :
                        'text-yellow-600 dark:text-yellow-400'
                      }`}>
                        {' '}{task.status}
                      </span>
                      • Due: {task.dueDate}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    task.priority === 'High' ? 'bg-red-100 text-red-800' :
                    task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {task.priority}
                  </span>
                </button>
              ))}
              {getCurrentUserTasks().length === 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">No recent activity</p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Customer Service Chatbot - Available globally for CS team */}
      {(userRole === 'customer_service' || userRole === 'supervisor' || userRole === 'manager' || userRole === 'cb_supervisor' || userRole === 'cm_supervisor' || userRole === 'system_admin') && (
        <CustomerServiceChatbot
          userRole={userRole}
          userName="CS Agent"
          userDepartment="Customer Service"
          onClientLookup={(clientId) => {
            // Use persisted client data (dynamic)
            const allClients = persistedClientRecords.get();
            const client = allClients.find(c => c.id === clientId);
            if (client) {
              setSelectedClient(client);
              setCurrentView('client-detail');
            }
          }}
          onEscalation={(issue) => {
            console.log('Escalation requested:', issue);
            // Handle escalation logic here
          }}
          onTransfer={(department) => {
            console.log('Transfer to department:', department);
            // Handle department transfer logic here
          }}
          position="bottom-right"
        />
      )}

      {/* Keyboard Shortcuts Modal - Available globally */}
      <KeyboardShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={() => setIsShortcutsModalOpen(false)}
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryCachePersister,
        maxAge: 10 * 60 * 1000, // 10 minutes - match gcTime; restored cache older than this is discarded
        // Credit Block / Cybersweep use live Gobodo payloads; persisting those queries caused full reloads to
        // rehydrate stale rows (with refetchOnMount: false) until session/site storage was cleared.
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            const key = query.queryKey;
            if (Array.isArray(key) && key[0] === 'credit-block') {
              return key[1] === CREDIT_BLOCK_CASE_ROW_CLIENT_QUERY_KEY && query.state.status === 'success';
            }
            if (Array.isArray(key) && key[0] === 'cybersweep') return false;
            return query.state.status === 'success';
          },
        },
      }}
    >
      <ThemeProvider>
        <ToastProvider>
          <StandardToastProvider position="top-right">
            <ApplicationProvider>
              {/* PE-762: Switch User Provider and Banner for "View As User" feature */}
              <SwitchUserProvider>
                <AbilityProvider>
                  <SwitchUserBanner />
                  <BrowserRouter>
                    <AppRoutes />
                  </BrowserRouter>
                </AbilityProvider>
              </SwitchUserProvider>
            </ApplicationProvider>
          </StandardToastProvider>
        </ToastProvider>
      </ThemeProvider>
    </PersistQueryClientProvider>
  );
};

export default App;
